import uniq from 'lodash/uniq';
import { useMemo } from 'react';
import { influencer, outreach } from '@round/api';
import { useCountries, useOutreachRequestsInfinite } from '@round/creatorbase-helpers/api-hooks';
import { useTiktokAudiosListQueries } from '@round/platforms/tiktok';
import { PAGE_SIZE } from '../components/OutreachRequestsTable/helpers';

type Params = Omit<outreach.GetOutreachRequestsParams, 'page' | 'client_id'> & {
    /** The list is client-scoped, so until a client is picked there is nothing to ask for. */
    client_id: number | undefined;
};

export function useOutreachRequestsList({ client_id, ...params }: Params) {
    const list = useOutreachRequestsInfinite(
        // The non-null assertion rides on `enabled` — the query never runs without a client.
        { client_id: client_id!, page_size: PAGE_SIZE, ...params },
        { enabled: typeof client_id === 'number' }
    );

    // One batch call per loaded page rather than one per row, so a page that resolves late only
    // holds up its own rows.
    const audios = useTiktokAudiosListQueries(
        list.pages.map((page) => {
            const audioIds = uniq(page.results.flatMap((request) => request.targets.audios));
            return {
                params: { id: audioIds.join(','), page_size: audioIds.length },
                config: { enabled: audioIds.length > 0 },
            };
        }),
        {
            // Keyed by id across every page at once: two requests can name the same sound.
            combine: (queries) => ({
                audiosById: new Map(
                    queries.flatMap((query) => query.data?.results ?? []).map((audio) => [audio.id, audio])
                ),
                isLoadingForPage: (pageIndex: number) => Boolean(queries[pageIndex]?.isLoading),
            }),
        }
    );

    const { countries, isLoading: isLoadingCountries } = useCountries();
    const countriesById = useMemo(() => getCountriesById(countries), [countries]);

    const getIsAudiosLoading = (requestId: number) => {
        const pageIndex = list.pages.findIndex((page) => page.results.some((request) => request.id === requestId));
        return pageIndex === -1 ? false : audios.isLoadingForPage(pageIndex);
    };

    return {
        rows: list.requests,
        count: list.count,
        audiosById: audios.audiosById,
        getIsAudiosLoading,
        countriesById,
        isLoadingCountries,
        isLoading: list.isLoading,
        hasNextPage: list.hasNextPage,
        isFetchingNextPage: list.isFetchingNextPage,
        isFetchNextPageError: list.isFetchNextPageError,
        fetchNextPage: list.fetchNextPage,
        isError: list.isError,
        errorMessage: list.errorMessage,
    };
}

function getCountriesById(countries: influencer.InfluencerLocation[]): Map<number, influencer.InfluencerLocation> {
    return new Map(countries.map((country) => [country.id, country]));
}
